'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

let _ = t => t,
    _t,
    _t2,
    _t3;

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import H1 from 'UIComponents/elements/headings/H1';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FeaturesSection from 'pricing-pages-unified-ui/components/free-tools-page/FeaturesSection';
import GuaranteeSection from 'pricing-pages-unified-ui/components/free-tools-page/GuaranteeSection';
import { FEATURES_SECTION } from 'pricing-pages-unified-ui/constants/Breakpoints';
import FooterMicrocopy from 'pricing-pages-unified-ui/components/common/FooterMicrocopy';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { MON569_SCREENS, SUBSCREENS } from 'pricing-pages-unified-ui/constants/Tracking';
import Question from 'pricing-pages-unified-ui/components/common/Question';
import { withRouter } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import breakpoint from '../../utils/responsiveBreakpoints';
import EnvironmentWrapper from 'pricing-pages-unified-ui/components/EnvironmentWrapper';
import SignupButton from 'pricing-pages-unified-ui/components/public-only/SignupButton';
import { withDocumentTitle } from 'pricing-pages-unified-ui/utils/DocumentUtils';
import PhoneSupport from 'pricing-pages-unified-ui/components/common/PhoneSupport';
import SkeletonLoadWrapper from '../loading/SkeletonLoadWrapper';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { useTrackRetail } from '../../selectors/RetailSelectors';
import NavLayout, { HeaderBackdrop } from '../mon569-tabs/NavLayout';
import { FaqsConfig } from '../../constants/FaqsConfig';
import UIFlex from 'UIComponents/layout/UIFlex';
import { NavItemEnum } from '../../constants/Mon569SidenavConfig';
import Faq from '../common/Faq';
import { OLAF } from 'HubStyleTokens/colors';
import { isAppAuthSelector } from '../../selectors/ConfigSelectors';
const PageContainer = styled.div.withConfig({
  displayName: "Mon569FreeToolsPage__PageContainer",
  componentId: "jd7iw4-0"
})(["background-color:", ";"], OLAF);
const Container = styled.div.withConfig({
  displayName: "Mon569FreeToolsPage__Container",
  componentId: "jd7iw4-1"
})(["background-color:white;display:flex;align-items:center;flex-direction:column;width:100%;position:relative;", ";"], breakpoint.cs(_t || (_t = _`
    padding: 20px 20px 0;
  `)));
const ContentWrapper = styled.div.withConfig({
  displayName: "Mon569FreeToolsPage__ContentWrapper",
  componentId: "jd7iw4-2"
})(["margin-left:50px;", ";"], breakpoint.cs(_t2 || (_t2 = _`
    margin: auto;
  `)));
const MainHeader = styled(FormattedMessage).withConfig({
  displayName: "Mon569FreeToolsPage__MainHeader",
  componentId: "jd7iw4-3"
})(["text-align:center;font-weight:600;font-size:32px;margin-bottom:20px;line-height:40px;@media (max-width:", "px){font-size:29px;}"], FEATURES_SECTION);
const SubHeader = styled(FormattedMessage).withConfig({
  displayName: "Mon569FreeToolsPage__SubHeader",
  componentId: "jd7iw4-4"
})(["font-weight:300;font-size:14px;max-width:600px;@media (max-width:", "px){font-size:16px;}"], FEATURES_SECTION);
const PhoneSupportWrapper = styled.div.withConfig({
  displayName: "Mon569FreeToolsPage__PhoneSupportWrapper",
  componentId: "jd7iw4-5"
})(["width:450px;margin:", ";"], ({
  isInApp
}) => isInApp ? '160px auto 0' : '100px auto 0');
const Taglines = styled(H1).withConfig({
  displayName: "Mon569FreeToolsPage__Taglines",
  componentId: "jd7iw4-6"
})(["text-align:center;margin-right:20px;", " .tagline{display:block;}"], breakpoint.cs(_t3 || (_t3 = _`
    padding: 0 0.45em;
    text-align: left;
  `)));
const SignupButtonWrapper = styled(UIFlex).withConfig({
  displayName: "Mon569FreeToolsPage__SignupButtonWrapper",
  componentId: "jd7iw4-7"
})(["flex-direction:column;align-items:center;margin-top:40px;"]);

const Mon569FreeToolsPage = () => {
  useTrackRetail();
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: MON569_SCREENS.FREE_TOOLS_CRM
    });
  }, []);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isInApp = useSelector(isAppAuthSelector);
  return /*#__PURE__*/_jsxs(PageContainer, {
    children: [/*#__PURE__*/_jsx(HeaderBackdrop, {}), /*#__PURE__*/_jsx(NavLayout, {
      selectedNavItem: NavItemEnum.CRM,
      children: /*#__PURE__*/_jsxs(Container, {
        "data-selenium-id": "page-success",
        children: [/*#__PURE__*/_jsxs(ContentWrapper, {
          children: [/*#__PURE__*/_jsx(NavMarker, {
            name: "FREE_TOOLS_PAGE"
          }), /*#__PURE__*/_jsxs("div", {
            className: "display-flex align-center flex-column p-x-4",
            style: {
              paddingBottom: '50px'
            },
            children: [/*#__PURE__*/_jsx(MainHeader, {
              message: "pricingPage.pages.crm.mainHeader"
            }), /*#__PURE__*/_jsx(SubHeader, {
              message: "pricingPage.pages.crm.subHeader"
            }), /*#__PURE__*/_jsx(EnvironmentWrapper, {
              publicNode: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
                widths: [200],
                heights: [56],
                className: "m-top-5",
                isLoading: !hasPricingAndPurchasingSucceeded,
                children: /*#__PURE__*/_jsx(SignupButton, {
                  sourceId: "pricing-hero",
                  productLine: "crm",
                  className: "p-x-9 p-y-5 m-top-5",
                  ctaKey: 'pricingPage.signupCtas.getStartedFree'
                })
              })
            })]
          }), /*#__PURE__*/_jsx(FeaturesSection, {}), /*#__PURE__*/_jsx(GuaranteeSection, {
            className: "text-left"
          }), /*#__PURE__*/_jsx(PhoneSupportWrapper, {
            isInApp: isInApp,
            children: /*#__PURE__*/_jsx(PhoneSupport, {
              phoneSourceId: "crm-middle",
              screen: SUBSCREENS.FREE_TOOLS_CRM
            })
          })]
        }), /*#__PURE__*/_jsxs(Faq, {
          children: [FaqsConfig[NavItemEnum.CRM].map((_ref) => {
            let {
              questionKey
            } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, ["questionKey"]);

            return /*#__PURE__*/_jsx(Question, Object.assign({
              questionKey: questionKey
            }, passthroughOptions), questionKey);
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(EnvironmentWrapper, {
          publicNode: /*#__PURE__*/_jsxs(SignupButtonWrapper, {
            children: [/*#__PURE__*/_jsx(Taglines, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                className: "tagline",
                message: "pricingPage.pages.crm.mainHeader"
              })
            }), /*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
                widths: [200],
                heights: [56],
                className: "m-bottom-8",
                isLoading: !hasPricingAndPurchasingSucceeded,
                children: /*#__PURE__*/_jsx(SignupButton, {
                  sourceId: "pricing-bottom",
                  productLine: "crm",
                  className: "p-x-9 p-y-5 m-top-5 m-bottom-8",
                  ctaKey: 'pricingPage.signupCtas.getStartedFree'
                })
              })
            })]
          })
        })]
      })
    })]
  });
};

export default withRouter(withDocumentTitle(Mon569FreeToolsPage, 'free'));